import { FC, lazy, Suspense } from 'react';
import {
	BrowserRouter,
	Navigate,
	Route,
	Routes,
	useSearchParams,
} from 'react-router-dom';

import { TrueLayerRedirectHandlerRoute } from '@mopla/payment-flow';
import { WithPassengerRoutingHelpers } from '@mopla/ui';

import { AppListener } from '../../app/appListener';
import { NotificationListener } from '../../app/notificationListener';
import { PaymentFeatureContainer } from '../../app/PaymentFeatureContainer';
import { environment } from '../../environments/environment';
import { AccountDeleted } from '../pages/accountDeleted/AccountDeleted';
import { ChipcardRequest } from '../pages/chipcardRequest';
import { DownloadInvoices } from '../pages/downloadInvoices';
import { DTicketResumeSubscription } from '../pages/dticketResumeSubscription/DTicketResumeSubscription';
import { DTicketSubscriptionCancel } from '../pages/dticketSubscriptionCancel';
import { DTicketSubscriptionEdit } from '../pages/dticketSubscriptionEdit/DTicketSubscriptionEdit';
import { DTicketSubscriptionEditPause } from '../pages/dticketSubscriptionEditPause/DTicketSubscriptionEditPause';
import { DTicketSubscriptionPause } from '../pages/dticketSubscriptionPause/DTicketSubscriptionPause';
import { EditPaymentMethod } from '../pages/editPaymentMethod';
import { ManageBillingAddress } from '../pages/manageBillingAddress';
import { MarketingFlow } from '../pages/marketingFlow/MarketingFlow';
import { MyTicketDetail } from '../pages/myTicketDetail';
import { OperatorTicketDetail } from '../pages/operatorTicketDetail';
import { OperatorTickets } from '../pages/operatorTickets';
import { RootPage } from '../pages/root/RootPage';
import { SpenditPage } from '../pages/spendit/SpenditPage';
import { SubscribeDTicket } from '../pages/subscribeDTicket.tsx/SubscribeDTicket';
import { Ticketshop } from '../pages/ticketshop';
import { UserDTicket } from '../pages/userDticket/UserDTicket';
import { WelcomeScreenV2 } from '../pages/welcomeV2';

import LazyRoute from './LazyRoute';
import PrivateRoute from './PrivateRoute';
import WelcomeRoute from './WelcomeRoute';

const HomeScreen = lazy(
	() => import(/*webpackChunkName: "Home"*/ '../pages/home/Home')
);

const ConditionalRedirect: FC = () => {
	const [searchParams] = useSearchParams();
	const hasTrueLayerRedirect = searchParams.has('truelayer_redirect');

	if (hasTrueLayerRedirect) {
		return <TrueLayerRedirectHandlerRoute />;
	}

	return <Navigate to="/home" replace />;
};

const AppRoutes: FC = () => {
	return (
		<Suspense fallback={null}>
			<BrowserRouter basename={environment.basename}>
				<WithPassengerRoutingHelpers>
					<PaymentFeatureContainer>
						<AppListener />
						<NotificationListener />
						<Routes>
							<Route element={<RootPage />}>
								<Route
									path="home/*"
									element={
										<PrivateRoute>
											<LazyRoute>
												<HomeScreen />
											</LazyRoute>
										</PrivateRoute>
									}
								/>
								<Route
									path="welcome"
									element={
										<WelcomeRoute>
											<WelcomeScreenV2 />
										</WelcomeRoute>
									}
								/>
								<Route
									path="spendit"
									element={
										<PrivateRoute>
											<SpenditPage />
										</PrivateRoute>
									}
								/>
								<Route
									path="account-deleted"
									element={
										<WelcomeRoute>
											<AccountDeleted />
										</WelcomeRoute>
									}
								/>
								<Route
									path="deutschlandticket"
									element={
										<WelcomeRoute>
											<MarketingFlow type="dTicket" />
										</WelcomeRoute>
									}
								/>
								<Route
									path="my-way"
									element={
										<WelcomeRoute>
											<MarketingFlow type="myWay" />
										</WelcomeRoute>
									}
								/>
								{/** TODO: redesign-dticket - make decision about this route name */}
								<Route
									path="subscribe-dticket"
									element={
										<PrivateRoute>
											<SubscribeDTicket />
										</PrivateRoute>
									}
								/>
								<Route
									path="user-dticket"
									element={
										<PrivateRoute>
											<UserDTicket />
										</PrivateRoute>
									}
								/>
								<Route
									path="user-dticket/subscription-pause"
									element={
										<PrivateRoute>
											<DTicketSubscriptionPause />
										</PrivateRoute>
									}
								/>
								<Route
									path="user-dticket/edit-pause"
									element={
										<PrivateRoute>
											<DTicketSubscriptionEditPause />
										</PrivateRoute>
									}
								/>
								<Route
									path="user-dticket/subscription-edit"
									element={
										<PrivateRoute>
											<DTicketSubscriptionEdit />
										</PrivateRoute>
									}
								/>
								<Route
									path="user-dticket/subscription-cancel"
									element={
										<PrivateRoute>
											<DTicketSubscriptionCancel />
										</PrivateRoute>
									}
								/>
								<Route
									path="user-dticket/download-invoices"
									element={
										<PrivateRoute>
											<DownloadInvoices />
										</PrivateRoute>
									}
								/>
								<Route
									path="user-dticket/download-invoices/manage-billing-address"
									element={
										<PrivateRoute>
											<ManageBillingAddress />
										</PrivateRoute>
									}
								/>
								<Route
									path="user-dticket/edit-payment-method"
									element={
										<PrivateRoute>
											<EditPaymentMethod />
										</PrivateRoute>
									}
								/>

								<Route
									path="user-dticket/resume-subscription"
									element={
										<PrivateRoute>
											<DTicketResumeSubscription />
										</PrivateRoute>
									}
								/>
								<Route
									path="ticketshop"
									element={
										<PrivateRoute>
											<Ticketshop />
										</PrivateRoute>
									}
								/>
								<Route
									path="ticketshop/my-tickets/:purchaseId"
									element={
										<PrivateRoute>
											<MyTicketDetail />
										</PrivateRoute>
									}
								/>
								<Route
									path="ticketshop/my-tickets/:purchaseId/chipcard"
									element={
										<PrivateRoute>
											<ChipcardRequest />
										</PrivateRoute>
									}
								/>
								<Route
									path="ticketshop/:operatorId"
									element={
										<PrivateRoute>
											<OperatorTickets />
										</PrivateRoute>
									}
								/>
								<Route
									path="ticketshop/:operatorId/tickets/:ticketId"
									element={
										<PrivateRoute>
											<OperatorTicketDetail />
										</PrivateRoute>
									}
								/>
								<Route path="*" element={<ConditionalRedirect />} />
							</Route>
						</Routes>
					</PaymentFeatureContainer>
				</WithPassengerRoutingHelpers>
			</BrowserRouter>
		</Suspense>
	);
};

export default AppRoutes;
